import React from "react";
import EmployeeInfo from "../Cards/EmployeeInfo";
import StatusIndicator from "./status";
import Badge from "../Library/Badge";
import { describeApprovalSteps } from "../../Utils/approvalRuleCopy";

const personStatus = (person, step) => {
	if (person?.action === "approved") return { status: "approved" };
	if (person?.action === "rejected") return { status: "denied" };
	if (step?.action === "approved") return { label: "Not needed" };
	if (step?.current) return { status: "pending", label: "Waiting" };
	return { label: "Not yet" };
};

// The step badge already says Waiting now, Approved, or Not yet.
// A second badge is useful only when people on the same step differ,
// such as one person approved and the other is not needed.
const showPersonStatus = (person, step) => {
	if ((step?.people || []).length <= 1) return false;
	return person?.action === "approved" || person?.action === "rejected" || step?.action === "approved" || step?.action === "rejected";
};

const employeeFor = (person) => {
	if (person?.id) return person;
	return { fName: person?.label || "Approver", lName: "", email: "" };
};

const stepTitle = (step, index, steps, sequence) => {
	if (sequence) {
		const place = `Step ${index + 1} of ${steps.length}`;
		return step.anyOne ? `${place} · any one of these` : place;
	}
	if (steps.length > 1) return step.anyOne ? "Any one of these" : "Must approve";
	return step.anyOne ? "Any one of these" : "Approver";
};

function Approvers({ approvers = [], steps = [], inSequence = false, appliedPolicy = null }) {
	if (steps?.length) {
		const sequence = inSequence === true && steps.length > 1;
		const parallel = !sequence && steps.length > 1;
		const summary = describeApprovalSteps(steps, inSequence);
		const policyLine = appliedPolicy?.id
			? `${appliedPolicy.domain === "expense" ? "Expense" : "Travel"} policy · ${appliedPolicy.name} · ID ${appliedPolicy.id}`
			: "";
		return (
			<div>
				{policyLine ? <div className="mt-1 text-xxs text-secondary">{policyLine}</div> : null}
				{sequence ? <div className="mt-2 text-sm font-semibold">In sequence</div> : null}
				{parallel ? <div className="mt-2 text-sm font-semibold">At the same time</div> : null}
				{steps.length === 1 && steps[0].anyOne ? <div className="mt-2 text-sm font-semibold">Any one can approve</div> : null}
				{summary ? <div className="mt-1 text-sm text-secondary">{summary}</div> : null}
				<div className="mt-3 flex flex-col gap-2">
					{steps.map((step, index) => (
						<div key={step.index ?? index}>
							{sequence && index > 0 ? <div className="py-1 text-xxs font-semibold text-secondary">Then</div> : null}
							<div className="rounded-lg border border-surface p-3">
								<div className="flex items-center justify-between gap-2">
									<div className="text-xs font-semibold">{stepTitle(step, index, steps, sequence)}</div>
									{step.current ? (
										<Badge variant="warning">Waiting now</Badge>
									) : step.action === "approved" ? (
										<Badge variant="success">Approved</Badge>
									) : step.action === "rejected" ? (
										<Badge variant="danger">Declined</Badge>
									) : (
										<Badge>Not yet</Badge>
									)}
								</div>
								{step.anyOne ? <div className="mt-1 text-xxs text-secondary">Or: one approval from this group is enough.</div> : null}
								{step.people?.map((person, personIndex) => {
									const state = showPersonStatus(person, step) ? personStatus(person, step) : null;
									return (
										<div key={person.id || personIndex}>
											{step.anyOne && personIndex > 0 ? <div className="mt-1 text-xxs text-secondary">or</div> : null}
											<div className="mt-2 flex-center-between gap-2">
												<EmployeeInfo employee={employeeFor(person)} />
												{state ? <StatusIndicator status={state.status} label={state.label} /> : null}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	return approvers.map((approver, index) => (
		<div key={approver?.id || index} className="mt-2 flex-center-between">
			<EmployeeInfo employee={approver} />
			<StatusIndicator status={approver.status} />
		</div>
	));
}

export default Approvers;
